import { ResourceType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { BigQueryActionRequiredDialog } from '@/components/dialogs/bigQueryActionRequiredDialog';
import { GcpSapHanaVmActionRequiredDialog } from '@/components/dialogs/gcpSapHanaVmActionRequiredDialog';
import { SapHanaVmActionRequiredDialog } from '@/components/dialogs/sapHanaVmActionRequiredDialog';
import { SqlVmActionRequiredDialog } from '@/components/dialogs/sqlvmActionRequiredDialog';
import {
  InstancePanel,
  type ResourcePanelTabs,
} from '@/components/panels/instancePanel';
import { useDAL } from '@/contexts/useDAL';

import { ExcludeFromBackupDialog } from './excludeFromBackupDialog';
import { InventoryBulkPolicyAssignmentDialog } from './inventoryBulkPolicyAssignmentDialog';
import { useInstanceActions } from './inventoryInstanceActions';
import { TakeSnapshotDialog } from './takeSnapshotDialog';

export const InventoryInstancePanel = ({
  id,
  onClose,
  onBack,
  onRowContentChange,
  initialTab,
}: {
  id: string;
  initialTab: ResourcePanelTabs;
  onClose: () => void;
  onBack?: () => void;
  onRowContentChange?: () => Promise<void>;
}) => {
  const [assignBackupPolicyDialogOpen, setAssignBackupPolicyDialogOpen] =
    useState(false);
  const [excludeFromBackupDialogOpen, setExcludeFromBackupDialogOpen] =
    useState(false);
  const [takeSnapshotDialogOpen, setTakeSnapshotDialogOpen] = useState(false);
  const [actionRequiredDialogOpen, setActionRequiredDialogOpen] =
    useState(false);
  const dal = useDAL();

  const { isLoading, data: body, mutate } = dal.inventory.useResource(id);

  const onEntityChange = async () => {
    await onRowContentChange?.();
    await mutate();
  };

  const actions = useInstanceActions(
    body?.resource,
    onEntityChange,
    setAssignBackupPolicyDialogOpen,
    setExcludeFromBackupDialogOpen,
    setTakeSnapshotDialogOpen,
    setActionRequiredDialogOpen
  );

  return (
    <>
      <InstancePanel
        entity={body?.resource}
        onClose={onClose}
        onBack={onBack}
        actions={actions}
        isLoading={isLoading}
        onEntityChange={onEntityChange}
        initialTab={initialTab}
      />
      {assignBackupPolicyDialogOpen && (
        <InventoryBulkPolicyAssignmentDialog
          providerResourceIds={
            body?.resource.providerResourceId
              ? [body?.resource.providerResourceId]
              : []
          }
          resourceTypes={
            body?.resource.resourceType ? [body.resource.resourceType] : []
          }
          onClose={() => {
            setAssignBackupPolicyDialogOpen(false);
            void onEntityChange();
          }}
        />
      )}
      <ExcludeFromBackupDialog
        isOpen={excludeFromBackupDialogOpen}
        entities={body?.resource ? [body?.resource] : []}
        onClose={() => {
          setExcludeFromBackupDialogOpen(false);
          void onEntityChange();
        }}
      />
      {takeSnapshotDialogOpen && (
        <TakeSnapshotDialog
          entities={body?.resource ? [body?.resource] : []}
          onClose={() => {
            setTakeSnapshotDialogOpen(false);
            void onEntityChange();
          }}
        />
      )}
      {actionRequiredDialogOpen &&
        body?.resource?.resourceType ===
          ResourceType.AzureSqlVirtualMachine && (
          <SqlVmActionRequiredDialog
            resources={body.resource ? [body.resource] : []}
            onClose={() => {
              setActionRequiredDialogOpen(false);
            }}
            onRefreshResources={async () => {
              void onEntityChange();
            }}
          />
        )}
      {actionRequiredDialogOpen &&
        body?.resource?.resourceType === ResourceType.AzureSapHanaVm && (
          <SapHanaVmActionRequiredDialog
            resource={body.resource}
            onClose={() => {
              setActionRequiredDialogOpen(false);
            }}
            onRefreshResources={async () => {
              void onEntityChange();
            }}
          />
        )}
      {actionRequiredDialogOpen &&
        body?.resource?.resourceType === ResourceType.GcpSapHanaVm && (
          <GcpSapHanaVmActionRequiredDialog
            resource={body.resource}
            onClose={() => {
              setActionRequiredDialogOpen(false);
            }}
            onRefreshResources={async () => {
              void onEntityChange();
            }}
          />
        )}
      {actionRequiredDialogOpen &&
        body?.resource?.resourceType === ResourceType.GcpBigQuery && (
          <BigQueryActionRequiredDialog
            resources={body.resource ? [body.resource] : []}
            onClose={() => {
              setActionRequiredDialogOpen(false);
            }}
          />
        )}
    </>
  );
};
